import { api } from '@connected/api-client';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useForm } from '@tanstack/react-form';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { useCallback, useMemo } from 'react';

import { queryClient } from '@/lib/query-client';

import type {
    Device,
    DeviceFieldName,
    DeviceFormValues,
    DeviceHookConfig,
    DeviceHookResult,
    DeviceKind,
    DevicePatcher,
    DevicesFormState,
    SelectOption,
} from '../types/hook-types';
import type { SearchParams } from '../utils/optimistic-updates';

import { getFieldConfigs, getVisibleFields } from '../config/field-config';
import { getFieldOptions as getFieldOptionsForValue, isFieldDisabledByDependency } from '../config/field-dependencies';
import { createDevicePayload, createDevicePayloads } from '../utils/device-factory';
import { mapDeviceToFormValues } from '../utils/mappers';
import {
    handleCreateOptimisticUpdate,
    handleDeleteOptimisticUpdate,
    handlePatchOptimisticUpdate,

} from '../utils/optimistic-updates';

/**
 * Gets the specific ModelState validation error message for a given field, if any.
 */
function getFieldError(fieldName: string, error: unknown): string | undefined {
    if (typeof error !== 'object' || error === null)
        return 'Een onbekende fout heeft zich voorgedaan (1)';

    if (!('ModelState' in error))
        return 'Een onbekende fout heeft zich voorgedaan (2)';

    const errorWithModelState = error as { ModelState: Record<string, unknown> };
    if (!(`command.${fieldName}` in errorWithModelState.ModelState))
        return 'Een onbekende fout heeft zich voorgedaan (3)';

    const modelState = errorWithModelState.ModelState[`command.${fieldName}`];

    if (!Array.isArray(modelState))
        return 'Een onbekende fout heeft zich voorgedaan (4)';
    if (modelState.length === 0)
        return 'Een onbekende fout heeft zich voorgedaan (5)';

    return modelState.join(', ');
}

/**
 * Factory function to create device-specific hooks.
 * Each device kind (RCD, RCCB, FCPD) gets its own hook with the appropriate field configuration.
 */
export function createDeviceHook(deviceKind: DeviceKind) {
    return function useDevice(config: DeviceHookConfig): DeviceHookResult {
        const {
            electricalProjectVersionId,
            electricalProjectDistributionBoardId,
            enableExtendedRiso,
            enableImpedanceMeasurementsPage = true,
        } = config;

        // Search params for optimistic updates
        const searchParams: SearchParams = useMemo(() => ({
            electricalProjectVersionId,
            electricalProjectDistributionBoardId,
            deviceKind,
        }), [electricalProjectVersionId, electricalProjectDistributionBoardId]);

        // Fetch all devices for the project
        const { data: devicesData, isLoading } = useSuspenseQuery(
            elektraAppQueryOptions.project.distributionBoardDeviceListOptions({
                electricalProjectVersionId,
            }),
        );

        // Filter devices by kind, board, and not deleted
        const devices = useMemo(
            () => (devicesData?.Devices ?? []).filter(
                (d): d is Device => d.DeviceKind === deviceKind
                    && d.ElectricalProjectDistributionBoardId === electricalProjectDistributionBoardId
                    && !d.IsDeleted,
            ),
            [devicesData?.Devices, electricalProjectDistributionBoardId],
        );

        // Fetch device options (dropdown values)
        const { data: options } = useQuery(
            elektraAppQueryOptions.project.inspection.distributionBoard.options.residualCurrentDeviceOptionsListOptions(),
        );

        // Form key for remounting when devices change (key-based form reinitialization)
        const formKey = useMemo(
            () => devices.map(d => d.ElectricalProjectDistributionBoardDeviceId).join(','),
            [devices],
        );

        // Field configuration based on device kind and feature flags
        const fieldOptions = useMemo(() => ({
            enableExtendedRiso,
            enableImpedanceMeasurementsPage,
        }), [enableExtendedRiso, enableImpedanceMeasurementsPage]);

        const fields = useMemo(
            () => getFieldConfigs(deviceKind, fieldOptions),
            [fieldOptions],
        );

        const visibleFields = useMemo(
            () => getVisibleFields(deviceKind, fieldOptions),
            [fieldOptions],
        );

        // Create form with array of device values
        const form = useForm<DevicesFormState>({
            defaultValues: {
                devices: devices.map(d => mapDeviceToFormValues(d)),
            },
            validatorAdapter: zodValidator(),
        });

        // Create mutation for adding devices
        const createMutation = api.useMutation(
            'post',
            '/api/v2/electrical/projects/distributionboards/devices/create',
            {
                onMutate: variables => handleCreateOptimisticUpdate(searchParams, variables!),
                onError: async (_data, _variables, context: any) => {
                    await queryClient.invalidateQueries({ queryKey: context?.listQueryKey });
                },
                scope: {
                    id: electricalProjectVersionId,
                },
            },
        );

        // Delete mutation
        const deleteMutation = api.useMutation(
            'delete',
            '/api/v2/electrical/projects/distributionboards/devices/delete',
            {
                onMutate: (variables) => {
                    const deviceId = variables?.params?.query?.ElectricalProjectDistributionBoardDeviceId;
                    if (deviceId) {
                        return handleDeleteOptimisticUpdate(searchParams, deviceId);
                    }
                },
                onSettled: async (_data, _variables, context: any) => {
                    await queryClient.invalidateQueries({ queryKey: context?.listQueryKey });
                },
                scope: {
                    id: electricalProjectVersionId,
                },
            },
        );

        // Patch mutation - used by createPatcher
        const patchMutation = api.useMutation(
            'patch',
            '/api/v2/electrical/projects/distributionboards/devices/patch',
            {
                scope: {
                    id: electricalProjectVersionId,
                },
            },
        );

        // Patcher factory - creates a patcher for a specific device
        const createPatcher = useCallback((deviceId: string): DevicePatcher => {
            const patchField = <TField extends keyof DeviceFormValues>(field: TField) =>
                async ({ value }: { value: DeviceFormValues[TField] }): Promise<string | undefined> => {
                    // Map field name to API format
                    const fieldNameMap: Record<keyof DeviceFormValues, string> = {
                        id: 'ElectricalProjectDistributionBoardDeviceId',
                        identifier: 'Identifier',
                        residualCurrentDeviceType: 'ResidualCurrentDeviceType',
                        ratedCurrent: 'RatedCurrent',
                        tripDelay: 'TripDelay',
                        isTestButtonFunctional: 'IsTestButtonFunctional',
                        fuseKind: 'FuseKind',
                        trippingCharacteristic: 'TrippingCharacteristic',
                        breakerRatedCurrent: 'BreakerRatedCurrent',
                        powerPhaseKind: 'PowerPhaseKind',
                        risoSign: 'RisoSign',
                        risoValue: 'RisoValue',
                        // Impedance loop fields
                        ziResistance: 'ZiResistance',
                        ziCurrent: 'ZiCurrent',
                        ziMinimalCurrent: 'ZiMinimalCurrent',
                        zsResistance: 'ZsResistance',
                        zsCurrent: 'ZsCurrent',
                        zsMinimalCurrent: 'ZsMinimalCurrent',
                    };

                    const apiFieldName = fieldNameMap[field];

                    // Find current device and compare values - skip if unchanged
                    const currentDevice = devices.find(
                        d => d.ElectricalProjectDistributionBoardDeviceId === deviceId,
                    );
                    if (currentDevice) {
                        const currentValue = currentDevice[apiFieldName as keyof Device];
                        if (currentValue === value) {
                            return undefined; // No change, skip patch
                        }
                    }

                    // Handle optimistic update
                    const context = await handlePatchOptimisticUpdate(
                        searchParams,
                        deviceId,
                        { body: { [apiFieldName]: value } },
                    );

                    try {
                        await patchMutation.mutateAsync({
                            body: {
                                ElectricalProjectDistributionBoardDeviceId: deviceId,
                                [apiFieldName]: value,
                            },
                        });
                    }
                    catch (error) {
                        // Rollback on error
                        queryClient.setQueryData(context.listQueryKey, context.listPreviousData);
                        return getFieldError(apiFieldName, error);
                    }
                    finally {
                        // Invalidate to ensure data consistency
                        await queryClient.invalidateQueries({ queryKey: context.listQueryKey });
                    }

                    return undefined;
                };

            patchField.debounceTime = 500 as const;

            return { patchField };
        }, [devices, searchParams, patchMutation]);

        // Field options getter (for use with form.Subscribe or useStore)
        const getFieldOptions = useCallback((fieldName: DeviceFieldName, deviceIndex: number): SelectOption[] => {
            const deviceValues = form.getFieldValue(`devices[${deviceIndex}]`);
            if (!deviceValues)
                return [];
            return getFieldOptionsForValue(fieldName, deviceValues, options, deviceKind);
        }, [form, options]);

        // Field disabled checker
        const isFieldDisabled = useCallback((fieldName: DeviceFieldName, deviceIndex: number): boolean => {
            const deviceValues = form.getFieldValue(`devices[${deviceIndex}]`);
            if (!deviceValues)
                return false;
            return isFieldDisabledByDependency(fieldName, deviceValues);
        }, [form]);

        // Actions - create, copy, delete
        const actions = useMemo(() => ({
            create: async () => {
                const payload = createDevicePayload({
                    electricalProjectDistributionBoardId,
                    deviceKind,
                    enableExtendedRiso,
                    existingDevices: devices,
                });
                await createMutation.mutateAsync({ body: payload });
            },

            copy: async (deviceId: string, count: number) => {
                const sourceDevice = devices.find(
                    d => d.ElectricalProjectDistributionBoardDeviceId === deviceId,
                );
                if (!sourceDevice)
                    return;

                const payloads = createDevicePayloads({
                    electricalProjectDistributionBoardId,
                    deviceKind,
                    enableExtendedRiso,
                    existingDevices: devices,
                    sourceDevice,
                }, count);

                // Create devices sequentially to ensure proper ordering
                for (const payload of payloads) {
                    await createMutation.mutateAsync({ body: payload });
                }
            },

            delete: async (deviceId: string) => {
                await deleteMutation.mutateAsync({
                    params: {
                        query: {
                            ElectricalProjectDistributionBoardDeviceId: deviceId,
                        },
                    },
                });
            },
        }), [devices, createMutation, deleteMutation, electricalProjectDistributionBoardId, enableExtendedRiso]);

        return {
            form,
            formKey,
            fields,
            visibleFields,
            devices,
            options,
            isLoading,
            getFieldOptions,
            isFieldDisabled,
            createPatcher,
            actions,
        };
    };
}
